
import {
  computed,
  defineComponent,
  PropType,
  shallowRef,
  ref,
  toRef,
  inject,
  watch,
  onMounted,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { ITabConfig } from "@/interfaces/iTabConfig";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods } from "@/helpers/data-format";
import { SiteApi } from "@/services/api/Site.service";
import { FormApi } from "@/services/api/Form.service";
import * as Yup from "yup";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import {
  siteFormsTableBulkDelete,
  siteFormsTableFilters,
  siteFormsTableHeaders,
} from "../siteDataTableOptions";
import { IForms, ISite } from "@/interfaces/iSite";
import { getLookups } from "@/services/api/Lookup.service";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { useToast } from "vue-toastification";
import { FORM_TABS } from "@/views/form/dataTableOptions";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import FormDetailModal from "@/views/form/modal/FormDetailModal.vue";
import { hasPermission } from "@/core/services/permission.service";
import { Emitter } from "mitt";
import { debounce } from "lodash";
interface ITabsEmit {
  tabs: ITabConfig[];
  title: string;
  icon?: string;
}
type Events = {
  VZTabsUpdate: ITabsEmit;
};

export default defineComponent({
  props: {
    data: { type: Object as PropType<ISite>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: { Datatable, VZDeleteModal, PreviewLink, FormDetailModal },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    let options: { label: any; value: string }[] = [];
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const showDetailDialog = ref<any>(false);
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showDeleteDialog = ref<any>(false);
    const showSearch = true;
    const tableData = ref<IForms[]>([]);
    const allTableData = ref<IForms[]>([]);
    const selectedForAction = ref<Partial<IForms>>({});
    const localPreference = ref({});
    const defaultFilter = ref({ status: "ACTIVE" });
    const bulkDelete = ref<IBulkDelete>(siteFormsTableBulkDelete());
    const tableHeader = ref(siteFormsTableHeaders());
    const tableFilter = ref(siteFormsTableFilters());
    const Datatable = ref();
    const emitter: Emitter<Events> | undefined = inject("emitter");
    const format = computed(() => {
      return FormatMethods;
    });
    const tabs = shallowRef<ITabConfig[]>(FORM_TABS());
    const viewName = "siteForms";
    const toast = useToast();
    const message = t("common.deleteSuccessMessage");
    const formAddedMsg = t("pages.user.formAssignedMsg");
    let formData = ref<IAddEditFormModel>({
      page: "Site Form",
      header: "Site Form",
      edit: false,
      data: null,
      validationSchema: Yup.object({
        formDefinitionId: Yup.array()
          .nullable()
          .min(1, t("pages.site.form") + " " + t("common.isRequired"))
          .required(t("pages.site.form") + " " + t("common.isRequired")),
      }),
      label: [{ addHeader: t("pages.site.assignForm") }],
      model: [
        {
          name: "formDefinitionId",
          label: t("pages.site.form"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select form",
          options: options,
          multiSelect: true,
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:BUSINESS:SITE:CREATE",
      delete: "VYZOR:BUSINESS:SITE:DELETE",
      update: "VYZOR:BUSINESS:SITE:UPDATE",
    });
    onMounted(async () => {
      // await refreshList();
      loading.value = true;
    });
    const getForms = async () => {
      await getLookups("forms/filter/FORM_NAME")
        .then((res: any) => {
          const data = res;
          options = [];
          data.map((obj: any) => {
            options.push({
              label: obj.text,
              value: obj.value,
            });
          });
          const formIds = allTableData.value.map((item) => Number(item.webId));
          const filteredValues = options.filter(
            (item) => !formIds.includes(Number(item.value))
          );
          formData.value.model[0].options = filteredValues;
        })
        .catch((err) => {
          return [];
        });
    };
    const edit = (item: any) => {
      formData.value.data = { ...item };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      loading.value = true;
      const data = reqData.formDefinitionId.map((item) => {
        return {
          formDefinitionId: item,
          siteId: dataProp.value.webId,
        };
      });
      await SiteApi.getInstance()
        .addSiteForm(data, formData.value.edit)
        .then(() => {
          toast.success(formAddedMsg);
          queryUpdate(dataProp.value.webId, { ...localPreference.value });
        })
        .catch((err) => {
          console.log("error in assigning form: ", err);
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (params?.status === "ALL") {
          params = {
            ...params,
            status: "",
          };
        } else if (!params || !params.status) {
          params = {
            ...params,
            ...defaultFilter.value,
          };
        }
        const data = await SiteApi.getInstance().getSiteFormsList(
          dataProp.value.webId,
          {
            ...params,
          }
        );
        const allData = await SiteApi.getInstance().getSiteFormsList(
          dataProp.value.webId,
          {
            status: "ACTIVE",
            size: 10000,
          }
        );
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        allTableData.value = [...allData.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
        getForms();
      } finally {
        loading.value = false;
      }
    };
    const onBulkDelete = async (ids) => {
      let deletedMsg = "Selected form(s) " + message;
      await SiteApi.getInstance()
        .deleteSiteForms(dataProp.value.webId, ids)
        .then(() => {
          toast.success(deletedMsg);
          refreshList();
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          showDeleteDialog.value = false;
          loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };

    const openDetailModal = async (site) => {
      showDetailDialog.value = true;
      selectedForAction.value = {};
      const form = await FormApi.getInstance().getFormById(site.webId);
      selectedForAction.value = form;
    };

    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;

    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        allTableData.value = [];
        lastFetchedWebId = webId;
        rowsPerPage.value = INNER_PAGE_ROWS_PER_PAGE;
        queryUpdate(+webId, {
          page: page.value - 1,
          size: rowsPerPage.value,
        });
        if (Datatable.value.VZFilter) {
          Datatable.value.VZFilter.getSavedFilterList();
        }
      }
    }, 300);

    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          // await queryUpdate(dataProp.value.webId, {
          //   page: page.value - 1,
          //   size: rowsPerPage.value,
          // });
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
          allTableData.value = [];
        }
      }
    );
    return {
      bulkDelete,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      tabs,
      total,
      rowsPerPage,
      loading,
      selectedForAction,
      syncQuery,
      formData,
      submit,
      refreshList,
      viewName,
      edit,
      onBulkDelete,
      openDetailModal,
      permissionModel,
      hasPermission,
      showDeleteDialog,
      showDetailDialog,
      Datatable,
      t,
      defaultFilter,
    };
  },
});
